import { useValidateEmailIsUniqueMutation } from '@shared/services/users';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Input from '@components/Input';
import { mapLocaleCodeToCountry } from '@features/WordPress/utilities/helpers/locale';
import { InputGroup } from '@features/Checkout/components/CheckoutSteps/CheckoutSteps.module.css';
import PhoneInput from '@components/PhoneInput';
import BusinessInputFields from '@features/Checkout/components/BusinessInputFields';

import { useRouter } from 'next/router';

import { useTranslation } from 'next-i18next';

const NewUserForm = ({ control, getValues, errors }) => {
  const { t } = useTranslation(['account', 'common', 'checkout']);
  const {
    business = {},
    user: { email, emailConfirm, phone } = {},
    isBusiness,
  } = useSelector((state) => state.checkout);
  const [validateEmailIsUnique] = useValidateEmailIsUniqueMutation();
  const { locale } = useRouter();

  return (
    <>
      {isBusiness && (
        <BusinessInputFields
          name={business.name}
          vatNumber={business.vatNumber}
          inputProps={{ inputWrapperClassName: InputGroup }}
          control={control}
          locale={locale}
        />
      )}
      <Controller
        name="email"
        control={control}
        defaultValue={email || ''}
        rules={{
          required: true,
          pattern: {
            value:
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            message: t('invalidEmail', { ns: 'common' }),
          },
          validate: {
            isUnique: async (mail) =>
              validateEmailIsUnique(mail)
                .unwrap()
                .then(() => {
                  return true;
                })
                .catch(() => {
                  return t('duplicateEmail', { ns: 'common' });
                }),
          },
        }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            type="text"
            label="E-mail"
            placeholder={t('yourEmail', { ns: 'common' })}
            error={fieldState.error}
            inputWrapperClassName={InputGroup}
          >
            <Controller
              name="emailConfirm"
              control={control}
              defaultValue={emailConfirm}
              rules={{
                required: true,
                validate: (value) =>
                  value === getValues('email') ||
                  t('emailNotMatching', { ns: 'common' }),
              }}
              render={({ field: fieldTwo, fieldState: fieldStateTwo }) => (
                <Input
                  type="text"
                  placeholder={t('confirmEmail', { ns: 'common' })}
                  readableName={t('confirmEmail', { ns: 'common' })}
                  {...fieldTwo}
                  error={fieldStateTwo.error}
                />
              )}
            />
          </Input>
        )}
      />

      {/* minLength is 11 because of whitespace in the formatted string */}
      <PhoneInput
        name="phone"
        control={control}
        country={mapLocaleCodeToCountry(locale)}
        defaultValue={phone || ''}
        error={errors.phone}
      />

      <Controller
        name="password"
        control={control}
        defaultValue=""
        rules={{
          required: true,
          minLength: {
            value: 8,
            message: t('minPasswordLength', { ns: 'account' }),
          },
        }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            type="password"
            label={t('password', { ns: 'common' })}
            placeholder={t('yourPassword', { ns: 'account' })}
            tooltip={t('passwordUsedFor', { ns: 'checkout' })}
            error={fieldState.error}
            inputWrapperClassName={InputGroup}
          />
        )}
      />
    </>
  );
};

export default NewUserForm;
